import axios from "../../api/axios";
import hbscan from "../../api/hbscan";
import IUser from "../../models/user";
import Auth from "../auth/AuthService";
import IUserRegistrationRequest from "../../models/requests/user/userRegistrationRequest";
import { ELanguage } from "../../models/enums/language";
import IPasswordResetRequest from "../../models/requests/user/passwordResetRequest";
import IUpdateUserRequest from "../../models/requests/user/updateUserRequest";
import i18next from "i18next";
import { availableLanguages } from "../../i18n";
import ILastSession from "../../models/userLastSession";
import { ERole } from "../../models/enums/role";
import { IUpdatedUser } from "../../pages/table/user/UserEditPage";
import qs from "qs";
import ILoginResponse from "../../models/loginResponse";

class UserService {
    PostLogin = async (username: string, pwd: string) => {
        const LOGIN_URL = "/api/hblogin/user/postlogin";
        const language = availableLanguages.indexOf(i18next.language) + 1;
        return axios.post<string>(
            LOGIN_URL,
            JSON.stringify({ email: username, password: pwd }),
            { params: { language: language } }
        );
    };

    MicrosoftLogin = async (accessToken: string) => {
        const URL = "/api/hblogin/sso/microsoftlogin";
        return (await axios.get<ILoginResponse>(
            URL,
            { headers: { Authorization: accessToken } }
        )).data;
    }

    GetUserByGuid = async (guid: string) => {
        const USER_URL = "api/hblogin/getuser";
        const BUILDING_URL = "api/hblogin/getbuildingsbyuser";

        var requestedUser: IUser | null = null;
        const response = await axios.get(`${USER_URL}/${guid}`);
        const user = response?.data;
        const buildings = (
            await axios.get(`${BUILDING_URL}/${guid}/${user.id}`)
        )?.data;
        requestedUser = {
            guid: guid,
            name: user.name,
            email: user.email,
            tenantId: user.tenantId,
            companyId: user.companyId,
            role: user.role,
            id: user.id,
            imageUrl:
                user.imageUrl ?? "../../assets/images/ProfilePlaceholder.jpg",
            buildingId: buildings[0].id,
            language: user.language,
            phone: user.phone,
            isLocked: user.isLocked,
            buildingIds: []
        };

        return requestedUser;
    };

    GetLogout = async () => {
        const LOGOUT_URL = "api/hblogin/logout";

        await axios.get(`${LOGOUT_URL}/${Auth.guid}`, {
            headers: { Authorization: Auth.guid },
        });
    };

    PostRegister = async (
        name: string,
        code: string,
        isAgreeWithTerms: boolean,
        isAgreeWithNewsletter: boolean,
        langauge: ELanguage,
        email?: string,
        password?: string,
        role?: ERole,
        managerId?: number
    ) => {
        const REGISTRATION_URL = "/api/hblogin/user/PostRegistration";
        const registration: IUserRegistrationRequest = {
            name: name,
            email: email,
            password: password,
            code: code,
            isAgreeWithTerms: isAgreeWithTerms,
            isAgreeWithNewsletter: isAgreeWithNewsletter,
            language: langauge,
            role: role,
            managerId: managerId,
        };

        return (
            await axios.post<IUser>(
                REGISTRATION_URL,
                JSON.stringify(registration)
            )
        ).data;
    };

    CreateUser = async (
        name: string,
        code: string,
        isManualCode: boolean,
        isAgreeWithTerms: boolean,
        isAgreeWithNewsletter: boolean,
        language: ELanguage,
        email?: string,
        password?: string,
        role?: ERole,
        managerId?: number,
        PropertyGroupIds?: number[],
        acombaEmployeeId?: number,
        acombaFunctionNumber?: number
    ) => {
        const REGISTRATION_URL = "/api/hbscan/user/CreateUser";
        const registration: IUserRegistrationRequest = {
            name,
            code,
            isManualCode,
            email,
            password,
            isAgreeWithTerms,
            isAgreeWithNewsletter,
            language,
            role,
            managerId,
            propertyGroupIds: PropertyGroupIds,
            acombaEmployeeId,
            acombaFunctionNumber
        };

        return (
            await hbscan.post<IUser>(
                REGISTRATION_URL,
                JSON.stringify(registration), {
                headers: { Authorization: Auth.guid },
            }
            )
        ).data;
    };


    PostResetPasswordEmail = async (email: string) => {
        const RESET_EMAIL_URL = `/api/hblogin/forgottenpassword/${email}`;

        return (await axios.post(RESET_EMAIL_URL)).data;
    };

    PostResetPasswordCode = async (email: string, code: string) => {
        const RESET_CODE_URL = `/api/hblogin/verifyinformationpassword/${email}/${code}`;

        return (await axios.post<boolean>(RESET_CODE_URL)).data;
    };

    PostResetPassword = async (
        dtoResetPassword: IPasswordResetRequest
    ) => {
        const RESET_CODE_URL = `/api/hblogin/newpassword`;

        return (await axios.post(RESET_CODE_URL, JSON.stringify(dtoResetPassword))).data;
    };

    GetUsers = async (guid: string, tenantId: number) => {
        const USER_URL = "api/hblogin/getusersbytenant";

        return (await axios.get<IUser[]>(`${USER_URL}/${guid}/${tenantId}`))
            .data;
    };

    PutUserUpdate = async (updateUserRequest: IUpdateUserRequest) => {
        const URL = `/api/hblogin/user/putuser`;
        await axios.put(URL, JSON.stringify(updateUserRequest), {
            headers: { Authorization: Auth.guid },
        });
    };

    GetHasRecoveryPassword = async (guid: string) => {
        const URL = `/api/hblogin/user/GetHasRecoveryPassword`;

        return (
            await axios.get<boolean>(URL, { headers: { Authorization: guid } })
        ).data;
    };

    PostRecoveryPassword = async (password: string) => {
        const URL = `/api/hblogin/user/PostRecoveryPassword`;

        return (
            await axios.get<void>(URL, {
                params: { password },
                headers: { Authorization: Auth.guid },
            })
        ).data;
    };

    GetUsersByBuilding = async (buildingId: number) => {
        const URL = `/api/hblogin/user/GetUsersByBuilding`;

        return (
            await axios.get<IUser[]>(URL, {
                params: { buildingId },
                headers: { Authorization: Auth.guid },
            })
        ).data;
    };

    GetUsersByManager = async (managerId: number) => {
        const URL = `/api/hblogin/user/GetUsersByManager`;

        return (
            await axios.get<IUser[]>(URL, {
                params: { managerId },
                headers: { Authorization: Auth.guid },
            })
        ).data;
    };

    GetUsersByCompany = async () => {
        const URL = `/api/hblogin/user/GetUsersByCompany`;

        return (
            await axios.get<IUser[]>(URL, {
                params: { companyId: Auth.user?.companyId },
                headers: { Authorization: Auth.guid },
            })
        ).data || [];
    };

    GetLastSessionsByUserIds = async (userIds: number[]) => {
        const chunkSize = 100;
        const lastSessions: ILastSession[] = [];

        for (let i = 0; i < userIds.length; i += chunkSize) {
            const chunk = userIds.slice(i, i + chunkSize);
            const URL = "/api/hblogin/user/GetLastSessionsByUserIds";

            const response = await axios.get<ILastSession[]>(URL, {
                headers: { Authorization: Auth.guid },
                params: { userIds: chunk },
                paramsSerializer: (params) => {
                    return qs.stringify(params, { arrayFormat: 'repeat' });
                },
            });

            if (response && response.data && Array.isArray(response.data)) {
                lastSessions.push(...response.data);
            }
        }

        return lastSessions;


    };

    GetUserById = async (userId: number) => {
        const URL = `/api/hblogin/user/GetUserById`;

        return (
            await axios.get<IUser>(URL, {
                headers: { Authorization: Auth.guid },
                params: { userId },
            })
        ).data;
    };

    PutUser = async (user: IUpdatedUser) => {
        const URL = `/api/hblogin/user/PutSpecificUser`;
        return (
            await axios.put<IUser>(URL, JSON.stringify(user), {
                headers: { Authorization: Auth.guid },
            })
        ).data;
    };

    PutResetPassword = async (user: IUpdatedUser) => {
        const URL = `/api/hblogin/user/PutResetPassword`;

        return await axios.put(URL, JSON.stringify(user), {
            headers: { Authorization: Auth.guid },
        });
    };

    IsUserMicrosoftAccount = async (email: string) => {
        const URL = `/api/hblogin/user/IsUserMicrosoftAccount`;

        return (
            await axios.get<boolean>(URL, {
                headers: { Authorization: Auth.guid },
                params: { email },
            })
        ).data;
    }

    PostRevalidateUser = async (username: string, pwd: string) => {
        const LOGIN_URL = "/api/hblogin/user/postrevalidateuser";
        return (
            await axios.post<boolean>(
                LOGIN_URL,
                JSON.stringify({ email: username, password: pwd }),
                { headers: { Authorization: Auth.guid } }
            )
        ).data
    };

    GenerateUserCode = async () => {
        const URL = "/api/hbscan/user/GenerateUserCode";

        return (await hbscan.get<string>(URL, { headers: { Authorization: Auth.guid } })).data;
    };
}

const userService = new UserService();

export default userService;
